import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../Config/config';
import { useTenant } from '../Context/TenantContext';
import LockedPopup from '../components/LockedPopup1';
import { trackFeatureUsage } from '../utils/analyticsTracker';

import { useSubscription } from '../hooks/useSubscription';
import PremiumUpgradeModal from '../components/PremiumUpgradeModal';
import { categorizedTechnologies } from '../AdminComponents/techData';

const QUESTIONS_PER_PAGE = 20;

const TechnologyDetail = ({ mode = "learner" }) => {
  const { slug } = useParams();
  const [questions, setQuestions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { tenant } = useTenant();
  const { canAccessFeature, isLoaded } = useSubscription();
  const navigate = useNavigate();

  const isTenantHost = window.location.hostname !== 'theskillgenie.com' && 
                      window.location.hostname !== 'www.theskillgenie.com' && 
                      window.location.hostname !== 'localhost' &&
                      window.location.hostname !== '127.0.0.1';

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isCollegeUser = user?.role === 'student' && (user?.tenant || user?.college_id || user?.institution_id);

  const isTenantMode = mode === "tenant" || !!tenant || isTenantHost || isCollegeUser;

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user?.userId || user?.user_id;
        
        // We use the tech index or specific slug to check access
        // For simplicity, we check if it's one of the first 5 in the category
        // But here we only have the slug. We need to find the index of this tech in its category.
        // For now, let's assume the backend or a helper provides this.
        // Actually, the requirement says "First 5 technology files/topics only".
        
        const res = await axios.get(`${API_BASE_URL}/questions-technology?technology=${slug}`);
        setQuestions(res.data || []);
        
        // ... analytics ...
      } catch (err) {
        console.error('Failed to load questions', err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [slug]);

  // Check access for the current tech
  // If we can't easily find the index, we might need a list of first 5 techs
  // Or handle it in the parent component. 
  // Given the structure, let's assume if it's not locked by the parent (TechnologyList), it's fine.
  // But we should still enforce it here if someone deep-links.
  
  // For deep-links, we'll need the categorizedTechnologies to find the index
  const getTechIndex = () => {
    // This is a bit heavy, but ensures deep-link protection
    for (const cat in categorizedTechnologies) {
      const index = categorizedTechnologies[cat].findIndex(t => t.title.toLowerCase().replace(/\s+/g, '-') === slug.toLowerCase());
      if (index !== -1) return index;
    }
    return 999; // Assume locked if not found
  };

  const hasAccess = isTenantMode || canAccessFeature('TECH_EXPLORER_MCQ', { techIndex: getTechIndex() });

  const totalPages = Math.ceil(questions.length / QUESTIONS_PER_PAGE);
  const startIndex = (currentPage - 1) * QUESTIONS_PER_PAGE;
  const visibleQuestions = questions.slice(startIndex, startIndex + QUESTIONS_PER_PAGE);

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  if (loading) return <div className="text-white p-6">Loading questions...</div>;

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-bgMain flex flex-col items-center justify-center p-6 text-center">
        <div className="bg-bgSection p-10 rounded-3xl border border-primary/30 shadow-2xl max-w-lg">
           <div className="text-6xl mb-6">🔒</div>
           <h2 className="text-3xl font-bold text-white mb-4">Content Locked</h2>
           <p className="text-gray-400 mb-8">
             Free users can access only the first 5 technologies. Upgrade to premium for unlimited access to all TechExplorer MCQs.
           </p>
           <button 
             onClick={() => navigate('/subscription-plans')}
             className="bg-primary text-white font-bold py-3 px-8 rounded-xl hover:bg-secondary transition-all"
           >
             Upgrade Now
           </button>
        </div>
      </div>
    );
  }

 return (
 <div className="p-6 bg-bgSection text-body min-h-screen">
 <h1 className="text-2xl font-bold mb-6">Questions: {slug.replace(/-/g, ' ')}</h1>

 {visibleQuestions.length === 0 ? (
 <p className="text-muted">No questions found for this technology.</p>
 ) : (
 visibleQuestions.map((q, i) => (
 <div key={q.id || i} className="mb-4 bg-bgSection p-4 rounded border border-gray-600">
 <p className="text-primary font-semibold">Q{startIndex + i + 1}: {q.question}</p>
 <ul className="mt-2 text-sm list-disc pl-5 text-white">
 <li>A. {q.option_a}</li>
 <li>B. {q.option_b}</li>
 <li>C. {q.option_c}</li>
 <li>D. {q.option_d}</li>
 </ul>
 </div>
 ))
 )}

 {/* Pagination Controls */}
 {questions.length > QUESTIONS_PER_PAGE && (
 <div className="flex justify-center items-center gap-4 mt-10">
 <button
 onClick={() => goToPage(currentPage - 1)}
 disabled={currentPage === 1}
 className="px-4 py-2 bg-secondary text-black rounded hover:bg-primary disabled:opacity-40"
 >
 Prev
 </button>
 <span className="text-white">
 Page {currentPage} of {totalPages}
 </span>
 <button
 onClick={() => goToPage(currentPage + 1)}
 disabled={currentPage === totalPages}
 className="px-4 py-2 bg-secondary text-black rounded hover:bg-primary disabled:opacity-40"
 >
 Next
 </button>
 </div>
 )}
 </div>
 );
};

export default TechnologyDetail;



